import { ColDef, ColGroupDef, GridOptions } from 'ag-grid-community'
import { UserDetail, LoginStatus } from 'api'
import { Translation, Locale } from 'locale'
import {
  gridOptionsBase,
  groupingRowColDef,
  TypedValueGetterParams,
  withAllContext,
  createGroupedActionRenderer,
  createActionsRenderer,
  StandardGridColumnId,
  StatusCellRenderer
} from 'view/components/datagrid'
import { makeBooleanFormatter, makeBooleanQuickFilterTextGetter, makeDateAndTimeFormatter } from 'view/shared/datagrid'
import { UserActionsButton } from './UserActionsButton'
import { getUserStatusColor, getUserStatusText } from './UserStatusRenderer'

export enum UsersGridColumnId {
  USER_CAN_TRADE = 'USER_CAN_TRADE',
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  USER_STATUS = 'USER_STATUS',
  USER_USER_NAME = 'USER_USER_NAME',
  USER_FIRST_NAME = 'USER_FIRST_NAME',
  USER_LAST_NAME = 'USER_LAST_NAME',
  ORGANISATION_NAME = 'ORGANISATION_NAME',
  PHONE_NUMBER = 'PHONE_NUMBER',
  SHORT_ID = 'SHORT_ID',
  NOTES = 'NOTES',
  OKTA_STATUS = 'OKTA_STATUS',
  OKTA_LAST_LOGIN = 'OKTA_LAST_LOGIN'
}

export const usersColumnDefs: (ColGroupDef | ColDef)[] = [
  {
    colId: StandardGridColumnId.CHECKBOX,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    checkboxSelection: true,
    width: 36,
    maxWidth: 36,
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockPosition: true,
    lockVisible: true,
    filter: false,
    resizable: false,
    sortable: false
  },
  groupingRowColDef,
  {
    colId: StandardGridColumnId.ACTIONS,
    headerName: Locale.translate(Translation.TC_EMPTY_HEADER),
    equals: () => false,
    width: 130,
    minWidth: 130,
    headerComponent: withAllContext(createGroupedActionRenderer(UserActionsButton)),
    cellClass: 'action-cell',
    cellRenderer: withAllContext(createActionsRenderer(UserActionsButton)),
    pinned: 'left',
    lockPinned: true,
    enableRowGroup: false,
    lockVisible: true,
    filter: false,
    sortable: false
  },
  {
    colId: UsersGridColumnId.USER_STATUS,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && getUserStatusText(params.data),
    headerName: Locale.translate(Translation.TC_STATUS),
    width: 100,
    filter: 'agSetColumnFilter',
    cellRenderer: StatusCellRenderer,
    cellRendererParams: {
      getStatusColor: getUserStatusColor
    },
    equals: () => false,
    lockVisible: true
  },
  {
    colId: UsersGridColumnId.USER_USER_NAME,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.username,
    headerName: Locale.translate(Translation.TC_USER_NAME),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.USER_CAN_TRADE,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.canTrade,
    headerName: Locale.translate(Translation.TC_TRADING_ACTIVE),
    width: 125,
    filter: 'agSetColumnFilter',
    valueFormatter: makeBooleanFormatter({ defaultValue: false }),
    getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
  },
  {
    colId: UsersGridColumnId.USER_LOGGED_IN,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.loginStatus === LoginStatus.LOGGED_IN,
    headerName: Locale.translate(Translation.TC_LOGGED_IN),
    width: 125,
    filter: 'agSetColumnFilter',
    valueFormatter: makeBooleanFormatter({ defaultValue: false }),
    getQuickFilterText: makeBooleanQuickFilterTextGetter({ defaultValue: false })
  },
  {
    colId: UsersGridColumnId.USER_FIRST_NAME,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.firstName,
    headerName: Locale.translate(Translation.TC_FIRST_NAME),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.USER_LAST_NAME,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.lastName,
    headerName: Locale.translate(Translation.TC_LAST_NAME),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.ORGANISATION_NAME,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) =>
      params.data && params.data.organisation && params.data.organisation.legalName,
    headerName: Locale.translate(Translation.TC_ORGANISATION),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.PHONE_NUMBER,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.phoneNumber,
    headerName: Locale.translate(Translation.TC_PHONE_NUMBER),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.SHORT_ID,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.shortId,
    headerName: Locale.translate(Translation.TC_SHORT_ID),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.NOTES,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.notes,
    headerName: Locale.translate(Translation.TC_NOTES),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.OKTA_STATUS,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.oktaUserStatus,
    headerName: Locale.translate(Translation.TC_OKTA_STATUS),
    width: 150,
    filter: 'agSetColumnFilter'
  },
  {
    colId: UsersGridColumnId.OKTA_LAST_LOGIN,
    valueGetter: (params: TypedValueGetterParams<UserDetail>) => params.data && params.data.user.oktaLastLogin,
    headerName: Locale.translate(Translation.TC_OKTA_LAST_LOGIN),
    width: 150,
    filter: 'agSetColumnFilter',
    valueFormatter: makeDateAndTimeFormatter()
  }
]

export const usersGridOptions: GridOptions<UserDetail> = {
  ...gridOptionsBase,
  getRowId: ({ data }) => data.user.id
}
